import { useEffect, useRef, useState } from "react";
import WindowSearch from "@/components/WindowSearch";
import { createPortal } from "react-dom";
import { toast } from "sonner";
import { Loader2, X, Play, Sparkles, Mic } from "lucide-react";
import { api } from "../lib/api";

const Slider = ({ label, value, onChange, min = 0, max = 1, step = 0.05, testid }) => (
  <div>
    <div className="mb-1 flex items-center justify-between text-[11px] text-[#A1A1AA]"><span>{label}</span><span className="font-plex text-white">{Number(value).toFixed(2)}</span></div>
    <input data-testid={testid} type="range" min={min} max={max} step={step} value={value} onChange={(e) => onChange(parseFloat(e.target.value))} className="w-full accent-[#00F0FF]" />
  </div>
);

export const TtsPanel = ({ open, onClose, initialText = "", projectId, atTime = null, onGenerated }) => {
  const [text, setText] = useState(initialText);
  const [voices, setVoices] = useState([]);
  const [models, setModels] = useState([{ id: "eleven_multilingual_v2", name: "Multilingual v2 (best quality)" }, { id: "eleven_turbo_v2_5", name: "Turbo v2.5 (faster, cheaper)" }]);
  const [voiceId, setVoiceId] = useState("");
  const [modelId, setModelId] = useState("eleven_multilingual_v2");
  const [language, setLanguage] = useState("");
  const [stability, setStability] = useState(0.5);
  const [similarity, setSimilarity] = useState(0.75);
  const [style, setStyle] = useState(0);
  const [speed, setSpeed] = useState(1);
  const [loadingVoices, setLoadingVoices] = useState(false);
  const [notConfigured, setNotConfigured] = useState(false);
  const [busy, setBusy] = useState(false);
  const [previewing, setPreviewing] = useState(false);
  const audioRef = useRef(null);

  useEffect(() => { if (open) setText(initialText || ""); }, [open, initialText]);

  useEffect(() => {
    if (!open) return;
    setLoadingVoices(true); setNotConfigured(false);
    api.getVoices()
      .then((d) => { setVoices(d.voices || []); if (d.models) setModels(d.models); if (d.voices?.[0]) setVoiceId((v) => v || d.voices[0].voice_id); })
      .catch((e) => { if (e?.response?.status === 400) setNotConfigured(true); else toast.error("Could not load voices."); })
      .finally(() => setLoadingVoices(false));
  }, [open]);

  if (!open) return null;

  const settings = { stability, similarity, style, speed };
  const body = () => ({ text: text.trim(), voice_id: voiceId, voice_name: voices.find((v) => v.voice_id === voiceId)?.name, model_id: modelId, language: language || null, voice_settings: settings });

  const preview = async () => {
    if (!text.trim() || !voiceId) { toast.error("Enter text and pick a voice."); return; }
    setPreviewing(true);
    try {
      const d = await api.previewTts(body());
      if (audioRef.current) { audioRef.current.src = d.audio_url; await audioRef.current.play(); }
    } catch (e) { toast.error(e?.response?.data?.detail || "Preview failed."); }
    setPreviewing(false);
  };

  const generate = async () => {
    if (!text.trim() || !voiceId) { toast.error("Enter text and pick a voice."); return; }
    setBusy(true);
    try {
      const d = await api.generateTts(projectId, body());
      onGenerated && onGenerated(d.asset, { atTime });
      toast.success(d.cached ? "Reused a matching voice clip" : "Voice generated");
      onClose && onClose();
    } catch (e) { toast.error(e?.response?.data?.detail || "Voice generation failed."); }
    setBusy(false);
  };

  return createPortal(
    <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/70 p-4" onClick={onClose} data-testid="tts-panel">
      <div className="relative w-full max-w-lg rounded-2xl border border-white/10 bg-[#0d0d0d] p-5 text-white shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <WindowSearch />
        <div className="mb-4 flex items-center justify-between">
          <div className="flex items-center gap-2 font-display text-lg font-black"><Sparkles size={18} className="text-[#00F0FF]" /> Text to Speech</div>
          <button data-testid="tts-close" onClick={onClose} className="rounded-lg p-1 text-[#A1A1AA] hover:text-white"><X size={18} /></button>
        </div>

        {notConfigured ? (
          <div className="rounded-xl border border-amber-500/30 bg-amber-500/5 p-4 text-sm text-amber-200" data-testid="tts-not-configured">
            ElevenLabs isn’t connected yet. Add your <span className="font-bold">ELEVENLABS_API_KEY</span> on the server to enable AI voices. Everything else in the editor keeps working.
          </div>
        ) : (
          <div className="space-y-4">
            <div>
              <label className="mb-1 block text-[11px] uppercase tracking-wider text-[#A1A1AA]">Text</label>
              <textarea data-testid="tts-text" value={text} onChange={(e) => setText(e.target.value)} rows={3} placeholder="Type or paste text to speak…" className="w-full resize-none rounded-lg border border-white/10 bg-white/[0.03] px-3 py-2 text-sm outline-none focus:border-[#00F0FF]/50" />
              <div className="mt-1 text-right text-[10px] text-[#666]">{text.length}/5000</div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="mb-1 block text-[11px] uppercase tracking-wider text-[#A1A1AA]">Voice</label>
                <select data-testid="tts-voice" value={voiceId} onChange={(e) => setVoiceId(e.target.value)} disabled={loadingVoices} className="w-full rounded-lg border border-white/10 bg-[#141414] px-3 py-2 text-sm outline-none focus:border-[#00F0FF]/50">
                  {loadingVoices && <option>Loading…</option>}
                  {voices.map((v) => <option key={v.voice_id} value={v.voice_id}>{v.name}{v.labels?.accent ? ` · ${v.labels.accent}` : ""}</option>)}
                </select>
              </div>
              <div>
                <label className="mb-1 block text-[11px] uppercase tracking-wider text-[#A1A1AA]">Model</label>
                <select data-testid="tts-model" value={modelId} onChange={(e) => setModelId(e.target.value)} className="w-full rounded-lg border border-white/10 bg-[#141414] px-3 py-2 text-sm outline-none focus:border-[#00F0FF]/50">
                  {models.map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}
                </select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-x-5 gap-y-3">
              <Slider label="Stability" value={stability} onChange={setStability} testid="tts-stability" />
              <Slider label="Similarity" value={similarity} onChange={setSimilarity} testid="tts-similarity" />
              <Slider label="Style" value={style} onChange={setStyle} testid="tts-style" />
              <Slider label="Speed" value={speed} onChange={setSpeed} min={0.7} max={1.2} step={0.05} testid="tts-speed" />
            </div>
            {atTime != null && <div className="rounded-lg border border-[#00F0FF]/20 bg-[#00F0FF]/5 px-3 py-2 text-xs text-[#00F0FF]">Will be placed on the timeline at {Number(atTime).toFixed(2)}s</div>}
            <audio ref={audioRef} className="hidden" />
            <div className="flex items-center justify-end gap-2 pt-1">
              <button data-testid="tts-preview" onClick={preview} disabled={previewing || busy} className="flex items-center gap-1.5 rounded-full border border-white/15 px-4 py-2 text-sm font-semibold text-white hover:border-[#00F0FF]/60 disabled:opacity-50">{previewing ? <Loader2 size={14} className="animate-spin" /> : <Play size={14} />} Preview</button>
              <button data-testid="tts-generate" onClick={generate} disabled={busy || previewing} className="flex items-center gap-1.5 rounded-full bg-[#00F0FF] px-5 py-2 text-sm font-bold text-black hover:bg-[#00F0FF]/90 disabled:opacity-50">{busy ? <Loader2 size={14} className="animate-spin" /> : <Mic size={14} />} {atTime != null ? "Generate & Add" : "Generate Audio"}</button>
            </div>
          </div>
        )}
      </div>
    </div>,
    document.body
  );
};

export default TtsPanel;
